import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';

import StorageAreaForm from '../../LocationSetup/StorageAreaForm';
import { useWarehouseLocationLayout, useWarehouseStorageConfig } from 'hooks/Warehouse';
import styles from '../styles';

function StorageAreas({ warehouse_id }) {
  const { classes } = styles();
  const { storageConfig, loaded, reload } = useWarehouseStorageConfig(warehouse_id);
  const { layout, loaded: layoutLoaded } = useWarehouseLocationLayout(warehouse_id);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const locationOptions = layout?.locations ?? [];
  const locationNameById = new Map(locationOptions.map((location) => [location.id, location.name || location.id]));
  const storageAreas = storageConfig?.storageAreas ?? [];
  const totalAssigned = storageAreas.reduce((sum, area) => sum + (area.locations?.length ?? 0), 0);
  const emptyAreas = storageAreas.filter((area) => !area.locations?.length).length;

  const closeDialog = () => {
    setEditOpen(false);
    setSaving(false);
  };

  const handleSaved = () => {
    closeDialog();
    reload();
  };

  return (
    <Paper className={classes.Info} elevation={0}>
      <div className="info-header">
        <Typography variant="h5" color="secondary">
          Storage Areas
        </Typography>
        <Tooltip
          arrow
          title={
            layoutLoaded && !locationOptions.length
              ? 'Set up the warehouse locations first, then select them in the storage areas.'
              : ''
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setEditOpen(true)}
              disabled={!locationOptions.length}
            >
              Manage storage areas
            </Button>
          </span>
        </Tooltip>
      </div>
      <Typography variant="caption" color="textSecondary">
        Groups of locations — one per delivery day by default — that organize where containers are stored.
      </Typography>
      {loaded && !storageAreas.length && (
        <Box sx={{ color: 'red' }}>
          Storage areas must be set up to organize warehouse locations. SETUP NOW &gt;&gt;&gt;
        </Box>
      )}
      {storageAreas.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
            <Box sx={{ minWidth: 96 }}>
              <Typography variant="h4" color="primary" sx={{ lineHeight: 1 }}>
                {storageAreas.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                storage areas
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ flex: 1, minWidth: 200 }}>
              {`${totalAssigned === 1 ? '1 location' : `${totalAssigned} locations`} assigned across areas${
                emptyAreas ? ` · ${emptyAreas} empty` : ''
              }`}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1 }}>
            {storageAreas.map((area) => (
              <Paper key={area.name} variant="outlined" sx={{ p: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {area.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={area.locations?.length ?? 0}
                    color={area.locations?.length ? 'primary' : 'default'}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary" noWrap sx={{ display: 'block', mt: 0.5 }}>
                  {area.locations?.length
                    ? area.locations.map((id) => locationNameById.get(id) ?? id).join(', ')
                    : 'No locations'}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      <Dialog open={editOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle variant="h6">Storage Areas</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            Rename areas and choose the locations each one covers. A location can be assigned to multiple storage areas.
          </Typography>
          <StorageAreaForm
            formId="storage-area-form"
            warehouseId={warehouse_id}
            initialAreas={storageConfig?.storageAreas}
            locationOptions={locationOptions}
            onSuccess={handleSaved}
            onSubmittingChange={setSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="inherit" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="storage-area-form" loading={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default StorageAreas;
